import React, { useState, useEffect, useMemo } from "react";
import {
  Target, Plus, CheckCircle, Clock, AlertTriangle, ChevronDown,
  ChevronUp, Edit3, Trash2, Link, Calendar, FileText, X,
} from "lucide-react";
import {
  addTreatmentGoal, updateTreatmentGoal, saveTreatmentPlan, linkNoteToGoal,
} from "../services/individualService";

const GOAL_DOMAINS = [
  "Mental Health Stability",
  "Medication Compliance",
  "Substance Use Recovery",
  "Housing Stability",
  "Employment / Education",
  "Social Support / Community",
  "Daily Living Skills",
  "Crisis Prevention",
  "Physical Health",
  "Legal / Justice Involvement",
  "Financial Stability",
  "Family Relationships",
];

function ProgressBar({ pct, color }) {
  return (
    <div style={{ height: 4, borderRadius: 2, background: "rgba(99,179,237,0.06)", marginTop: 4 }}>
      <div style={{ height: "100%", borderRadius: 2, width: `${pct}%`, background: color, transition: "width 0.3s" }} />
    </div>
  );
}

export default function TreatmentPlanTab({ individual, notes, S }) {
  const [plan, setPlan] = useState(individual?.treatmentPlan || null);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [expandedGoal, setExpandedGoal] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [linkingGoal, setLinkingGoal] = useState(null);
  const [saving, setSaving] = useState(false);

  // New goal form
  const [newGoal, setNewGoal] = useState({
    domain: "",
    description: "",
    objectives: [""],
    targetDate: "",
    measurableCriteria: "",
  });

  useEffect(() => {
    setPlan(individual?.treatmentPlan || null);
  }, [individual?.treatmentPlan]);

  const goals = plan?.goals || [];

  // Stats
  const stats = useMemo(() => {
    const total = goals.length;
    const met = goals.filter(g => g.status === "met").length;
    const inProgress = goals.filter(g => g.status === "in-progress").length;
    const notAddressed = goals.filter(g => g.status === "not-addressed").length;
    const totalLinked = goals.reduce((s, g) => s + (g.linkedNotes?.length || 0), 0);
    return { total, met, inProgress, notAddressed, totalLinked };
  }, [goals]);

  const handleAddGoal = async () => {
    if (!newGoal.domain || !newGoal.description) return;
    setSaving(true);
    const result = await addTreatmentGoal(individual.id, {
      domain: newGoal.domain,
      description: newGoal.description,
      objectives: newGoal.objectives.filter(o => o.trim()),
      targetDate: newGoal.targetDate,
      measurableCriteria: newGoal.measurableCriteria,
    });
    if (result.success) {
      // Refresh from individual
      const updated = { ...plan || { goals: [], status: "active", createdAt: new Date().toISOString() } };
      updated.goals = [...(updated.goals || []), {
        id: `goal-${Date.now()}`,
        domain: newGoal.domain,
        description: newGoal.description,
        objectives: newGoal.objectives.filter(o => o.trim()),
        targetDate: newGoal.targetDate,
        measurableCriteria: newGoal.measurableCriteria,
        status: "in-progress",
        createdAt: new Date().toISOString(),
        linkedNotes: [],
      }];
      setPlan(updated);
      setNewGoal({ domain: "", description: "", objectives: [""], targetDate: "", measurableCriteria: "" });
      setShowAddGoal(false);
    }
    setSaving(false);
  };

  const handleStatusChange = async (goalId, newStatus) => {
    setSaving(true);
    await updateTreatmentGoal(individual.id, goalId, { status: newStatus });
    setPlan(prev => {
      const updated = { ...prev };
      updated.goals = updated.goals.map(g => g.id === goalId ? { ...g, status: newStatus } : g);
      return updated;
    });
    setSaving(false);
  };

  const handleLinkNote = async (goalId, note) => {
    setSaving(true);
    const summary = note.noteData?.responseToInterventions?.slice(0, 80) || note.serviceType || "Session note";
    await linkNoteToGoal(individual.id, goalId, note.id, summary);
    setPlan(prev => {
      const updated = { ...prev };
      updated.goals = updated.goals.map(g => {
        if (g.id === goalId) {
          const linked = [...(g.linkedNotes || [])];
          if (!linked.find(n => n.noteId === note.id)) {
            linked.push({ noteId: note.id, summary, date: new Date().toISOString() });
          }
          return { ...g, linkedNotes: linked };
        }
        return g;
      });
      return updated;
    });
    setLinkingGoal(null);
    setSaving(false);
  };

  const handleDeleteGoal = async (goalId) => {
    setSaving(true);
    const updated = { ...plan };
    updated.goals = updated.goals.filter(g => g.id !== goalId);
    await saveTreatmentPlan(individual.id, updated);
    setPlan(updated);
    setSaving(false);
  };

  const statusColor = (s) => s === "met" ? "#68d391" : s === "in-progress" ? "#63b3ed" : "#fc8181";
  const statusLabel = (s) => s === "met" ? "Met" : s === "in-progress" ? "In Progress" : "Not Addressed";

  return (
    <div>
      {/* No Plan Warning */}
      {goals.length === 0 && !showAddGoal && (
        <div style={{ ...S.card, background: "rgba(252,129,129,0.04)", border: "1px solid rgba(252,129,129,0.12)", textAlign: "center", padding: "32px 20px" }}>
          <AlertTriangle size={28} style={{ color: "#fc8181", margin: "0 auto 8px" }} />
          <div style={{ fontSize: 14, fontWeight: 800, color: "#fc8181", marginBottom: 4 }}>No Treatment Plan on File</div>
          <div style={{ fontSize: 11, color: "#718096", marginBottom: 16 }}>A treatment plan is required for Medicaid compliance. Add goals to create the plan.</div>
          <button onClick={() => setShowAddGoal(true)} style={{
            padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 700,
            background: "linear-gradient(135deg, #3182ce, #2b6cb0)", color: "white", border: "none", cursor: "pointer",
          }}>
            <Plus size={14} style={{ display: "inline", marginRight: 4 }} /> Create Treatment Plan
          </button>
        </div>
      )}

      {/* Stats */}
      {goals.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
          {[
            { label: "Goals", value: stats.total, color: "#63b3ed" },
            { label: "Met", value: stats.met, color: "#68d391" },
            { label: "Active", value: stats.inProgress, color: "#ecc94b" },
            { label: "Linked", value: stats.totalLinked, color: "#b794f4" },
          ].map((s, i) => (
            <div key={i} style={{ ...S.card, textAlign: "center", padding: 10, marginBottom: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
              <div style={{ fontSize: 8, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Overall progress */}
      {goals.length > 0 && (
        <div style={{ ...S.card, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#a0aec0" }}>Overall Progress</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: "#68d391" }}>
              {goals.length > 0 ? Math.round((stats.met / goals.length) * 100) : 0}%
            </span>
          </div>
          <ProgressBar pct={goals.length > 0 ? (stats.met / goals.length) * 100 : 0} color="#68d391" />
        </div>
      )}

      {/* Add Goal Button (when plan exists) */}
      {goals.length > 0 && !showAddGoal && (
        <button onClick={() => setShowAddGoal(true)} style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          width: "100%", padding: "10px 0", borderRadius: 10, fontSize: 12, fontWeight: 700,
          background: "rgba(49,130,206,0.08)", color: "#63b3ed",
          border: "1px dashed rgba(49,130,206,0.2)", cursor: "pointer", marginBottom: 12,
        }}>
          <Plus size={14} /> Add Goal
        </button>
      )}

      {/* Add Goal Form */}
      {showAddGoal && (
        <div style={{ ...S.card, border: "1px solid rgba(49,130,206,0.2)", marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: "#63b3ed" }}>New Treatment Goal</span>
            <button onClick={() => setShowAddGoal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#718096" }}>
              <X size={14} />
            </button>
          </div>

          {/* Domain */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Domain</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
            {GOAL_DOMAINS.map(d => (
              <button key={d} onClick={() => setNewGoal(g => ({ ...g, domain: d }))} style={{
                padding: "4px 10px", borderRadius: 8, fontSize: 10, fontWeight: 600, cursor: "pointer",
                background: newGoal.domain === d ? "rgba(49,130,206,0.2)" : "rgba(15,23,42,0.5)",
                color: newGoal.domain === d ? "#63b3ed" : "#718096",
                border: `1px solid ${newGoal.domain === d ? "rgba(49,130,206,0.3)" : "rgba(99,179,237,0.06)"}`,
              }}>{d}</button>
            ))}
          </div>

          {/* Goal description */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Goal Statement</div>
          <textarea
            style={{ width: "100%", padding: "8px 12px", borderRadius: 10, background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.08)", color: "#e2e8f0", fontSize: 12, outline: "none", resize: "vertical", minHeight: 60, marginBottom: 12 }}
            placeholder="e.g. Individual will maintain stable housing for 6 consecutive months"
            value={newGoal.description}
            onChange={e => setNewGoal(g => ({ ...g, description: e.target.value }))}
          />

          {/* Objectives */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Objectives (measurable steps)</div>
          {newGoal.objectives.map((obj, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <input
                style={{ flex: 1, padding: "6px 10px", borderRadius: 8, background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.08)", color: "#e2e8f0", fontSize: 11, outline: "none" }}
                placeholder={`Objective ${i + 1}`}
                value={obj}
                onChange={e => {
                  const updated = [...newGoal.objectives];
                  updated[i] = e.target.value;
                  setNewGoal(g => ({ ...g, objectives: updated }));
                }}
              />
              {newGoal.objectives.length > 1 && (
                <button onClick={() => setNewGoal(g => ({ ...g, objectives: g.objectives.filter((_, j) => j !== i) }))}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#fc8181" }}>
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
          <button onClick={() => setNewGoal(g => ({ ...g, objectives: [...g.objectives, ""] }))}
            style={{ fontSize: 10, color: "#63b3ed", background: "none", border: "none", cursor: "pointer", fontWeight: 700, marginBottom: 12 }}>
            + Add Objective
          </button>

          {/* Target date & criteria */}
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Target Date</div>
              <input type="date" value={newGoal.targetDate}
                onChange={e => setNewGoal(g => ({ ...g, targetDate: e.target.value }))}
                style={{ width: "100%", padding: "6px 10px", borderRadius: 8, background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.08)", color: "#e2e8f0", fontSize: 11, outline: "none" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>How to Measure</div>
              <input value={newGoal.measurableCriteria}
                onChange={e => setNewGoal(g => ({ ...g, measurableCriteria: e.target.value }))}
                placeholder="e.g. 3 consecutive clean UAs"
                style={{ width: "100%", padding: "6px 10px", borderRadius: 8, background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.08)", color: "#e2e8f0", fontSize: 11, outline: "none" }}
              />
            </div>
          </div>

          <button onClick={handleAddGoal} disabled={saving || !newGoal.domain || !newGoal.description}
            style={{
              width: "100%", padding: "10px 0", borderRadius: 10, fontSize: 13, fontWeight: 700,
              background: "linear-gradient(135deg, #38a169, #2f855a)", color: "white", border: "none", cursor: "pointer",
              opacity: saving || !newGoal.domain || !newGoal.description ? 0.5 : 1,
            }}>
            {saving ? "Saving..." : "Add Goal to Treatment Plan"}
          </button>
        </div>
      )}

      {/* Goals List */}
      {goals.map(goal => {
        const isExpanded = expandedGoal === goal.id;
        const linkedCount = goal.linkedNotes?.length || 0;
        const daysTilTarget = goal.targetDate ? Math.ceil((new Date(goal.targetDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;
        const isOverdue = daysTilTarget !== null && daysTilTarget < 0 && goal.status !== "met";

        return (
          <div key={goal.id} style={{
            ...S.card,
            borderLeft: `3px solid ${statusColor(goal.status)}`,
            marginBottom: 10,
          }}>
            {/* Goal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", cursor: "pointer" }}
              onClick={() => setExpandedGoal(isExpanded ? null : goal.id)}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(99,179,237,0.08)", color: "#63b3ed" }}>
                    {goal.domain}
                  </span>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: `${statusColor(goal.status)}15`, color: statusColor(goal.status) }}>
                    {statusLabel(goal.status)}
                  </span>
                  {isOverdue && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(252,129,129,0.15)", color: "#fc8181" }}>
                      Overdue
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0", marginTop: 6, lineHeight: 1.3 }}>
                  {goal.description}
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
                  {goal.targetDate && (
                    <span style={{ fontSize: 10, color: isOverdue ? "#fc8181" : "#718096" }}>
                      <Calendar size={9} style={{ display: "inline", marginRight: 3 }} />
                      {new Date(goal.targetDate).toLocaleDateString()}
                      {daysTilTarget !== null && daysTilTarget >= 0 && ` (${daysTilTarget}d left)`}
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: "#b794f4" }}>
                    <Link size={9} style={{ display: "inline", marginRight: 3 }} />
                    {linkedCount} note{linkedCount !== 1 ? "s" : ""} linked
                  </span>
                </div>
              </div>
              <div style={{ color: "#4a5568" }}>
                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </div>
            </div>

            {/* Expanded content */}
            {isExpanded && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(99,179,237,0.04)" }}>
                {/* Objectives */}
                {goal.objectives?.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Objectives</div>
                    {goal.objectives.map((obj, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "start", gap: 6, marginBottom: 4 }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: statusColor(goal.status), marginTop: 4, flexShrink: 0 }} />
                        <span style={{ fontSize: 11, color: "#a0aec0" }}>{obj}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Measurable criteria */}
                {goal.measurableCriteria && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Measurable Criteria</div>
                    <div style={{ fontSize: 11, color: "#a0aec0" }}>{goal.measurableCriteria}</div>
                  </div>
                )}

                {/* Linked notes */}
                {goal.linkedNotes?.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#b794f4", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Linked Session Notes</div>
                    {goal.linkedNotes.map((ln, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 6, background: "rgba(99,179,237,0.03)", marginBottom: 3 }}>
                        <FileText size={9} style={{ color: "#63b3ed" }} />
                        <span style={{ fontSize: 10, color: "#a0aec0", flex: 1 }}>{ln.summary}</span>
                        <span style={{ fontSize: 9, color: "#4a5568" }}>{new Date(ln.date).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {/* Status change buttons */}
                  {goal.status !== "met" && (
                    <button onClick={() => handleStatusChange(goal.id, "met")} style={{
                      display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                      background: "rgba(104,211,145,0.08)", color: "#68d391", border: "1px solid rgba(104,211,145,0.15)", cursor: "pointer",
                    }}>
                      <CheckCircle size={10} /> Mark Met
                    </button>
                  )}
                  {goal.status === "met" && (
                    <button onClick={() => handleStatusChange(goal.id, "in-progress")} style={{
                      display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                      background: "rgba(99,179,237,0.08)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.15)", cursor: "pointer",
                    }}>
                      <Clock size={10} /> Reopen
                    </button>
                  )}
                  {goal.status !== "not-addressed" && goal.status !== "met" && (
                    <button onClick={() => handleStatusChange(goal.id, "not-addressed")} style={{
                      display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                      background: "rgba(252,129,129,0.08)", color: "#fc8181", border: "1px solid rgba(252,129,129,0.15)", cursor: "pointer",
                    }}>
                      <AlertTriangle size={10} /> Not Addressed
                    </button>
                  )}

                  {/* Link note */}
                  <button onClick={() => setLinkingGoal(linkingGoal === goal.id ? null : goal.id)} style={{
                    display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                    background: "rgba(183,148,244,0.08)", color: "#b794f4", border: "1px solid rgba(183,148,244,0.15)", cursor: "pointer",
                  }}>
                    <Link size={10} /> Link Note
                  </button>

                  {/* Delete */}
                  <button onClick={() => { if (window.confirm("Remove this goal?")) handleDeleteGoal(goal.id); }} style={{
                    display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                    background: "rgba(252,129,129,0.04)", color: "#718096", border: "1px solid rgba(99,179,237,0.06)", cursor: "pointer",
                  }}>
                    <Trash2 size={10} /> Remove
                  </button>
                </div>

                {/* Link note picker */}
                {linkingGoal === goal.id && (
                  <div style={{ marginTop: 8, padding: 10, borderRadius: 10, background: "rgba(15,23,42,0.5)", border: "1px solid rgba(183,148,244,0.1)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#b794f4", marginBottom: 6 }}>Select a note to link:</div>
                    {notes.filter(n => n.status === "submitted" || n.status === "signed").length === 0 ? (
                      <div style={{ fontSize: 11, color: "#4a5568" }}>No submitted notes to link.</div>
                    ) : (
                      notes.filter(n => n.status === "submitted" || n.status === "signed").slice(0, 10).map(n => {
                        const alreadyLinked = goal.linkedNotes?.find(ln => ln.noteId === n.id);
                        return (
                          <button key={n.id} onClick={() => !alreadyLinked && handleLinkNote(goal.id, n)}
                            disabled={!!alreadyLinked}
                            style={{
                              display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "6px 8px",
                              borderRadius: 6, background: alreadyLinked ? "rgba(104,211,145,0.04)" : "rgba(99,179,237,0.03)",
                              border: "none", cursor: alreadyLinked ? "default" : "pointer", textAlign: "left", marginBottom: 3,
                            }}>
                            {alreadyLinked ? <CheckCircle size={10} style={{ color: "#68d391" }} /> : <FileText size={10} style={{ color: "#63b3ed" }} />}
                            <span style={{ fontSize: 10, color: "#a0aec0", flex: 1 }}>{n.date} · {n.serviceType}</span>
                            <span style={{ fontSize: 9, color: alreadyLinked ? "#68d391" : "#4a5568" }}>{alreadyLinked ? "Linked" : "Link"}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}